import { db } from "@/indexedDB/db";
import { MessageSendingStatus, type MessageEntity } from "@/indexedDB/types";
import { ChatType } from "@/server/types/chat";
import { heldUploadSlotCount, heldBlobSendCount } from "@/lib/uploadSlots";
import { chunkedSessionCount } from "@/zustand/UploadControlState";
import { blobSendSessionCount } from "@/middleware/hooks/attachments/blobSendQueue";
import { extractTimestampFromUUID7, isValidUUID7 } from "@/utils/uuid7";
import {
  STRANDED_RECONCILE_QUERY_ATTEMPTS,
  STRANDED_RECONCILE_QUERY_RETRY_MS,
  STRANDED_CONFIRM_MAX_GROUPS,
} from "@/config/constants";
import { confirmStrandedSends } from "./confirmSendByClientId";

/**
 * A row is "chunked" if any attachment slot carries an upload session. Media,
 * file (future chunked docs) and voice all share the AttachmentMeta shape with
 * `uploadState`, so all three are checked — the #1177 lookup must never touch a
 * chunked row (its completion is owned by the attachment-status feature).
 */
const hasUploadState = (m: MessageEntity): boolean =>
  !!(
    m.content?.media?.uploadState ||
    m.content?.file?.uploadState ||
    m.content?.voice?.uploadState
  );

/**
 * At startup, reconcile "stranded" outgoing sends — rows frozen on PENDING
 * because the tab died mid-send (OOM, crash, force-quit) so the send's `catch`
 * never ran. Nothing else cleans these up, so they spin "sending…" forever and
 * are even undeletable (the delete path and context menu both bail on PENDING).
 * Each stranded row is moved to its correct terminal state.
 *
 * Cases, told apart by whether the message reached the server and the chunked
 * upload state:
 *
 *  1. NOT published, NON-CHUNKED (id === _clientId, no `uploadState`): a
 *     single-blob send (document / image / voice / forward) or a plain text
 *     message. Whether it reached the server is UNKNOWABLE from local state:
 *     `id` is stamped only after the POST response is processed, so a POST that
 *     committed server-side but whose response died looks identical to one that
 *     never left. Instead of guessing, we CONFIRM against the server by
 *     `client_id` (#1177, `confirmStrandedSends`):
 *       - found       → the server has it → heal to DELIVERED/READ (never SENT,
 *                       mirroring the #898 status invariant), stamping the real
 *                       server id + timestamp.
 *       - not_found   → confirmed absent → FAILED (deletable, as before).
 *       - unconfirmed → offline / window not exhausted / lookup errored → parked
 *                       on FAILED with `deliveryUnconfirmed`, which stops the
 *                       spinner but suppresses delete and shows "Not confirmed
 *                       sent" instead of the red failed tick. Normal sync heals
 *                       it later if the message is re-fetched.
 *     Skipped while ANY send is live (see the liveness guard below).
 *
 *  2. Published (id !== _clientId) with `content.media.uploadState === "complete"`:
 *     a chunked send whose upload FINISHED server-side but whose terminal SENT
 *     stamp never landed → recover to SENT.
 *
 *  3. Published with `uploadState === "uploading"`, or NOT published but CHUNKED:
 *     a chunked send that died mid-upload → FAILED, but ONLY when no chunked
 *     upload is live. Chunked rows are deliberately NOT sent through the #1177
 *     lookup — the "did the bytes finish?" question is owned by the (separate)
 *     server-authoritative attachment-status feature; this branch keeps the
 *     pre-#1177 behaviour unchanged (dormant in prod: `chunkedUpload` is OFF).
 *
 * Liveness guards — a FAILED flip must never hit a send that is merely still
 * running in another tab. Two cross-tab signals (Web Locks, auto-released when a
 * tab dies), each with a per-tab fallback where locks are unsupported:
 *   - chunked uploads hold a capped slot lock (heldUploadSlotCount / session count)
 *   - single-blob sends hold a liveness beacon (heldBlobSendCount / in-flight count)
 * Neither count is per-message, so when anything is live we conservatively skip
 * the whole matching class this boot; it is re-evaluated next boot. The SENT
 * recovery (case 2) needs no guard — "complete" is terminal and unambiguous.
 *
 * The decision is re-derived from the row RE-READ inside the transaction (not the
 * scan snapshot), and applied only while it is still PENDING — so a concurrent
 * sync/ack, or a state change between scan and flip, is never clobbered.
 *
 * NOTE (accepted residual): the blob-send beacon covers the encrypt+upload span
 * but is released just before the message posts, so a cold boot in that sub-second
 * window can still transiently confirm-then-heal a live single-blob send. It
 * self-heals when that send completes and re-stamps the row.
 */
export async function reconcileStrandedSends(): Promise<void> {
  // Query the PENDING rows, retrying if the DB is momentarily unavailable. On the
  // iOS-PWA IDB open-recovery path the DB can be closed/reopened during boot, so
  // the first query rejects; a single try/catch would then silently no-op for the
  // ENTIRE session (the caller's once-per-boot guard is already burned). Retry so
  // the sweep still runs once the DB recovers.
  let rows: MessageEntity[] | undefined;
  for (
    let attempt = 0;
    attempt < STRANDED_RECONCILE_QUERY_ATTEMPTS;
    attempt++
  ) {
    try {
      // PENDING rows are stranded sends. FAILED rows still flagged
      // `deliveryUnconfirmed` are sends a PRIOR boot could not confirm (offline);
      // re-confirm them each boot so `unconfirmed` is TRANSIENT, not a permanent
      // undeletable state. Plain FAILED rows (no flag) are left alone — the
      // pre-#1177 field rows are an accepted residual (see docs).
      const pending = await db.messages
        .where("sendingStatus")
        .equals(MessageSendingStatus.PENDING)
        .toArray();
      const unconfirmed = await db.messages
        .where("sendingStatus")
        .equals(MessageSendingStatus.FAILED)
        .filter((m) => m.deliveryUnconfirmed === true)
        .toArray();
      rows = [...pending, ...unconfirmed];
      break;
    } catch {
      if (attempt < STRANDED_RECONCILE_QUERY_ATTEMPTS - 1) {
        await new Promise((r) =>
          setTimeout(r, STRANDED_RECONCILE_QUERY_RETRY_MS),
        );
      }
    }
  }
  if (!rows) {
    return; // DB never became available this boot — next reload reattempts
  }
  if (rows.length === 0) {
    return;
  }

  // Cross-tab liveness snapshots (Web Locks), with per-tab fallbacks where the
  // Web Locks API is unavailable. Sampled once for this boot's sweep.
  const activeChunkedUploads =
    (await heldUploadSlotCount().catch(() => null)) ?? chunkedSessionCount();
  const activeBlobSends =
    (await heldBlobSendCount().catch(() => null)) ?? blobSendSessionCount();
  const anythingLive = activeChunkedUploads > 0 || activeBlobSends > 0;

  // Bucket the rows. Non-chunked, never-published, not-live outgoing rows are
  // confirmed against the server by client_id (#1177) — both PENDING sends and
  // FAILED rows a prior boot left `unconfirmed`. Every other PENDING row keeps the
  // terminal logic below; a non-eligible unconfirmed row is left as-is (retried
  // next boot).
  const lookupCandidates: MessageEntity[] = [];
  const immediate: MessageEntity[] = [];
  for (const msg of rows) {
    const published = msg.id !== msg._clientId;
    const eligible =
      msg.isOutgoing && !published && !hasUploadState(msg) && !anythingLive;
    if (eligible) {
      lookupCandidates.push(msg);
    } else if (msg.sendingStatus === MessageSendingStatus.PENDING) {
      immediate.push(msg);
    }
  }

  // Immediate rows — the pre-#1177 per-row logic, unchanged.
  for (const msg of immediate) {
    try {
      await db.transaction("rw", db.messages, async () => {
        const fresh = await db.messages.get(msg._clientId);
        // Re-derive from the fresh row: skip if no longer PENDING (a concurrent
        // ack/sync won) or not an outgoing send of ours.
        if (
          !fresh ||
          fresh.sendingStatus !== MessageSendingStatus.PENDING ||
          !fresh.isOutgoing
        ) {
          return;
        }

        const published = fresh.id !== fresh._clientId;
        const uploadStatus = fresh.content?.media?.uploadState?.status;

        let target: MessageSendingStatus | null = null;
        if (!published) {
          // Never reached the server (per local state). Skip while any send is
          // live anywhere — it could be THIS row still running in another tab.
          if (!anythingLive) {
            target = MessageSendingStatus.FAILED;
          }
        } else if (uploadStatus === "complete") {
          // Upload finished; only the SENT stamp is missing → recover.
          target = MessageSendingStatus.SENT;
        } else if (uploadStatus === "uploading" && activeChunkedUploads === 0) {
          // Published-but-incomplete chunked upload, nothing live.
          target = MessageSendingStatus.FAILED;
        }

        if (target !== null) {
          await db.messages.update(fresh._clientId, { sendingStatus: target });
        }
      });
    } catch {
      // best-effort per row — a failed flip must not abort the rest
    }
  }

  // Non-chunked stranded sends — confirm against the server (#1177).
  await confirmAndApplyStrandedSends(lookupCandidates);
}

/**
 * Confirm the non-chunked stranded candidates against the server by `client_id`
 * and apply the verdicts. The network lookup runs OUTSIDE any transaction; each
 * apply re-reads inside its own transaction and re-checks the invariants so a
 * concurrent ack/publish is never clobbered.
 */
async function confirmAndApplyStrandedSends(
  candidates: MessageEntity[],
): Promise<void> {
  if (candidates.length === 0) {
    return;
  }

  // Cap the number of (chatId, threadId) groups probed per boot; overflow groups
  // stay PENDING and are retried next boot (bounds the boot network cost).
  const seen = new Set<string>();
  const capped: MessageEntity[] = [];
  for (const m of candidates) {
    const key = `${m.chatId} ${m.threadId ?? ""}`;
    if (!seen.has(key)) {
      if (seen.size >= STRANDED_CONFIRM_MAX_GROUPS) {
        continue;
      }
      seen.add(key);
    }
    capped.push(m);
  }

  const verdicts = await confirmStrandedSends(
    capped.map((m) => ({
      _clientId: m._clientId,
      chatId: m.chatId,
      threadId: m.threadId,
    })),
  );

  // Resolve chat types once per chat (outside the message transaction, which is
  // scoped to `db.messages` only) so the found-branch can pick DELIVERED vs READ.
  // Read concurrently — this is on the boot path. NB: an unknown chat type falls
  // through to READ, matching the #898 status invariant (syncBackfillDecrypt).
  const chatIds = [...new Set(capped.map((m) => m.chatId))];
  const chats = await Promise.all(
    chatIds.map((chatId) => db.chats.get(chatId).catch(() => undefined)),
  );
  const chatTypeById = new Map<string, ChatType | undefined>();
  chatIds.forEach((chatId, i) => chatTypeById.set(chatId, chats[i]?.type));

  for (const msg of capped) {
    const verdict = verdicts.get(msg._clientId);
    if (!verdict) {
      // Defensive: confirmStrandedSends returns a verdict for every candidate it
      // is given (overflow groups were already dropped from `capped`), so this is
      // not expected — skip rather than assume a state. The row stays PENDING and
      // is retried next boot.
      continue;
    }
    try {
      await db.transaction("rw", db.messages, async () => {
        const fresh = await db.messages.get(msg._clientId);
        // Re-derive inside the txn: skip if it changed under us — a live ack, a
        // publish in another tab, or it turned chunked. Applies to a still-PENDING
        // send OR a FAILED row a prior boot left `deliveryUnconfirmed` (re-confirm).
        // Never clobber.
        if (
          !fresh ||
          !fresh.isOutgoing ||
          fresh.id !== fresh._clientId ||
          hasUploadState(fresh) ||
          !(
            fresh.sendingStatus === MessageSendingStatus.PENDING ||
            (fresh.sendingStatus === MessageSendingStatus.FAILED &&
              fresh.deliveryUnconfirmed === true)
          )
        ) {
          return;
        }

        if (verdict.kind === "found") {
          // The server has it → it was delivered. Mirror the #898 status
          // invariant (syncBackfillDecrypt): P2P → DELIVERED, group → READ, never
          // SENT. Stamp the real server id so future syncs match it.
          const status =
            chatTypeById.get(fresh.chatId) === ChatType.P2P
              ? MessageSendingStatus.DELIVERED
              : MessageSendingStatus.READ;
          const healed: Partial<MessageEntity> = {
            id: verdict.serverId,
            sendingStatus: status,
            deliveryUnconfirmed: false,
          };
          // Only rewrite the timestamp from a VALID server id — an invalid one
          // would stamp the 2020 fallback and re-sort the row; keep the existing.
          if (isValidUUID7(verdict.serverId)) {
            healed.timestamp = extractTimestampFromUUID7(verdict.serverId);
          }
          await db.messages.update(fresh._clientId, healed);
        } else if (verdict.kind === "not_found") {
          // Confirmed absent (the fetched window covered the send's time) → a
          // genuine failure. Deletable, as before.
          await db.messages.update(fresh._clientId, {
            sendingStatus: MessageSendingStatus.FAILED,
            deliveryUnconfirmed: false,
          });
        } else {
          // Inconclusive → stop the spinner but do NOT assert failure: mark "not
          // confirmed", which suppresses delete and neutralises the label.
          await db.messages.update(fresh._clientId, {
            sendingStatus: MessageSendingStatus.FAILED,
            deliveryUnconfirmed: true,
          });
        }
      });
    } catch {
      // best-effort per row — a failed apply must not abort the rest
    }
  }
}
